import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { pillarArticles } from '@/mocks/articles';

export default function ArticlePage({ slug }: { slug?: string }) {
  const params = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const resolvedSlug = slug || params.slug;
  const article = pillarArticles.find((a) => a.slug === resolvedSlug);

  if (!article) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="w-full px-4 md:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-3xl font-bold text-stone-800 mb-4">Article not found</h1>
          <a href="/" className="text-emerald-700 hover:underline">← {t('breadcrumb_home')}</a>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-6">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 lg:px-8 mb-6">
          <nav className="text-sm text-stone-500">
            <a href="/" className="hover:text-emerald-700 transition-colors">{t('breadcrumb_home')}</a>
            <span className="mx-2">/</span>
            <span className="text-stone-700">{article.category}</span>
          </nav>
        </div>

        <article className="w-full px-4 md:px-6 lg:px-8 max-w-4xl mx-auto">
          {/* Header */}
          <header className="mb-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-stone-800 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-lg md:text-xl text-stone-600 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-4 mt-6 text-xs md:text-sm text-stone-400">
              <span>{t('article_published')}: {article.published}</span>
              <span>{t('article_updated')}: {article.updated}</span>
            </div>
          </header>

          {/* Quick Answer Box */}
          <div className="mb-10 p-5 md:p-6 rounded-xl bg-emerald-50 border border-emerald-100">
            <h2 className="text-lg md:text-xl font-semibold text-emerald-800 mb-3">
              {t('article_quick_answer')}
            </h2>
            <p className="text-sm md:text-base text-emerald-700 leading-relaxed">
              {article.quickAnswer}
            </p>
          </div>

          {/* Sections */}
          {article.sections.map((section, idx) => (
            <section key={section.heading} id={`section-${idx}`} className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4">
                {section.heading}
              </h2>
              {section.paragraphs.map((para, pIdx) => (
                <p key={pIdx} className="text-sm md:text-base text-stone-600 leading-relaxed mb-4">
                  {para}
                </p>
              ))}
            </section>
          ))}

          {/* Back to Home internal link */}
          <div className="mb-10">
            <a
              href="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-800 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              {t('breadcrumb_home')}
            </a>
          </div>

          {/* FAQ */}
          <section className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4">
              {t('faq_h2')}
            </h2>
            <div className="space-y-4">
              {article.faqs.map((faq, idx) => (
                <div key={idx} className="p-4 md:p-5 rounded-xl bg-stone-50 border border-stone-200">
                  <h3 className="text-sm font-semibold text-stone-800 mb-2">{faq.q}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Related Articles */}
          <section className="mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4">
              {t('article_related')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {article.related.map((rel) => (
                <a
                  key={rel.slug}
                  href={`/${rel.slug}/`}
                  className="flex items-center gap-2 p-3 rounded-lg bg-stone-50 border border-stone-200 hover:border-emerald-200 hover:bg-white transition-all"
                >
                  <i className="ri-arrow-right-line text-emerald-600 shrink-0" />
                  <span className="text-sm text-stone-700 hover:text-emerald-700 transition-colors">{rel.title}</span>
                </a>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="mb-12 p-6 md:p-8 rounded-xl bg-stone-50 border border-stone-200 text-center">
            <h3 className="text-xl md:text-2xl font-bold text-stone-800 mb-3">
              {t('final_cta_h2')}
            </h3>
            <p className="text-sm md:text-base text-stone-600 mb-6 max-w-xl mx-auto">
              {t('final_cta_text')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors text-sm md:text-base whitespace-nowrap"
              >
                CSSBuy Spreadsheet
              </a>
              <a
                href="/"
                className="px-6 py-3 bg-stone-100 text-stone-700 font-medium rounded-lg hover:bg-stone-200 transition-colors text-sm md:text-base whitespace-nowrap"
              >
                {t('breadcrumb_home')}
              </a>
            </div>
          </section>

          {/* Editorial Note */}
          <div className="p-4 md:p-5 rounded-lg bg-amber-50 border border-amber-100 mb-8">
            <p className="text-xs md:text-sm text-amber-700 leading-relaxed">
              <strong>Editorial Note:</strong> This article was last updated on {article.updated}. Information about products, prices, links and availability may have changed since then. We regularly review our content, but we recommend verifying all information directly before making any purchase. This website is an independent resource and does not officially represent CSSBuy.
            </p>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}